import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDecodeTokenMutation, useLoginMutation } from '../store/apiSlice';
import { useConfig } from '../hooks/useConfig';
import { useProductConfig } from '../context/ProductConfigContext';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../store/authSlice';
import { AUTH_MIDDLEWARE_CONFIG } from './authMiddlewareConfig';

/**
 * Middleware to handle query token and basic_cred authentication
 * @param {Object} options - Configuration options
 * @param {Array} options.routes - Routes where middleware should apply (defaults to AUTH_MIDDLEWARE_CONFIG.routes)
 * @param {Function} options.onAuthSuccess - Callback when authentication succeeds
 * @param {Function} options.onAuthError - Callback when authentication fails
 * @param {boolean} options.cleanUrlAfterProcessing - Whether to clean URL after processing (default: true)
 * @param {boolean} options.redirectToLoginOnFailure - Whether to redirect to login on failure (default: true)
 * @returns {Object} - Middleware state and handlers
 */
export const useAuthMiddleware = (options = {}) => {
  const {
    routes = AUTH_MIDDLEWARE_CONFIG.routes,
    excludedRoutes = AUTH_MIDDLEWARE_CONFIG.excludedRoutes,
    onAuthSuccess = AUTH_MIDDLEWARE_CONFIG.onAuthSuccess,
    onAuthError = AUTH_MIDDLEWARE_CONFIG.onAuthError,
    cleanUrlAfterProcessing = AUTH_MIDDLEWARE_CONFIG.cleanUrlAfterProcessing,
    redirectToLoginOnFailure = AUTH_MIDDLEWARE_CONFIG.redirectToLoginOnFailure
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { updateConfig } = useConfig();
  const { updateProductConfig } = useProductConfig();
  
  const [decodeToken] = useDecodeTokenMutation();
  const [login] = useLoginMutation();
  
  const isProcessingRef = useRef(false);
  const processedParamsRef = useRef(new Set());

  /**
   * Get the intended destination after authentication
   */
  const getIntendedDestination = () => {
    // First check if there's a stored intended destination
    const intendedDestination = localStorage.getItem('intended_destination');
    if (intendedDestination) {
      localStorage.removeItem('intended_destination');
      return intendedDestination;
    }
    
    // If no stored destination, check if we're on a protected route
    const currentPath = location.pathname;
    if (shouldApplyMiddleware() && currentPath !== '/login' && currentPath !== '/otp-verification') {
      return currentPath + location.search;
    }
    
    // Default to home
    return '/home';
  };

  /**
   * Check if current route should apply auth middleware
   */
  const shouldApplyMiddleware = () => {
    const currentPath = location.pathname;
    
    // Check if route is in excluded routes
    if (excludedRoutes.some(route => {
      if (route.endsWith('*')) {
        return currentPath.startsWith(route.slice(0, -1));
      }
      return currentPath === route || currentPath.startsWith(route + '/');
    })) {
      return false;
    }
    
    // Check if route is in included routes
    return routes.some(route => {
      if (route.endsWith('*')) {
        return currentPath.startsWith(route.slice(0, -1));
      }
      return currentPath === route || currentPath.startsWith(route + '/');
    });
  };

  /**
   * Process token from query parameters
   */
  const processToken = async (token, clientName = '', isKeycloak = false, shouldClearAuthFlag = true) => {
    try {
      const searchParams = new URLSearchParams(location.search);
      let urlToken = searchParams.get(AUTH_MIDDLEWARE_CONFIG.queryParams.token);
      let urlBasicCred = searchParams.get(AUTH_MIDDLEWARE_CONFIG.queryParams.basicCred);
      let urlKeycloak = searchParams.get(AUTH_MIDDLEWARE_CONFIG.queryParams.keycloak);
     
      if(urlToken && urlKeycloak && !isKeycloak && urlKeycloak === 'true')
        isKeycloak = true;
      
      if(urlToken && !isKeycloak && localStorage.getItem('idp_details'))
        localStorage.removeItem('idp_details');
      
      // Check if we already have a valid token
      const currentToken = localStorage.getItem('auth_token');
      if (currentToken === token && !urlToken && !urlBasicCred) {
        console.log("Token already exists, skipping processing");
        return { success: true, alreadyExists: true };
      }
      console.log("inside processToken")
      const decodedResponse = await decodeToken({
        token,
        client_name: clientName,
        keycloak: isKeycloak
      }).unwrap();
      // console.log("decodedResponse",decodedResponse)
      localStorage.setItem('auth_token', token);
      updateConfig(decodedResponse.data.config);
      localStorage.setItem('user_details', JSON.stringify({ 
        username: decodedResponse.data?.user, 
        email: decodedResponse.data?.email ?? decodedResponse.data?.user 
      }));
      // Set idp details for keycloak when keycloak is true in ifrmae pages
      if(isKeycloak) {
        localStorage.setItem('idp_details', JSON.stringify({idp_hint: 'google'}));
      }

      // Fetch and update product config after decode token
      try {
        await updateProductConfig(token);
      } catch (err) {
        console.error('Failed to fetch feature list:', err);
        // Continue with login even if feature list fetch fails
      }

      // if (decodedResponse.data.config?.otp_auth) {
      //   navigate('/otp-verification');
      //   // Don't clear auth process flag for OTP auth - it will be cleared after OTP verification
      // } else
       {
        dispatch(loginSuccess(decodedResponse.data));
        // Trigger storage change event to update other components
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'auth_token',
          newValue: token
        }));
        
        // Navigate to intended destination after successful authentication
        const intendedDestination = getIntendedDestination();
        // console.log('Navigating to intended destination:', intendedDestination);
        navigate(intendedDestination);
        
        // Clear auth process flag if requested
        if (shouldClearAuthFlag) {
          localStorage.removeItem('auth_process_active');
          localStorage.removeItem('auth_process_time');
          window.dispatchEvent(new CustomEvent('authProcessChange', { 
            detail: { isInAuthProcess: false } 
          }));
        }
      }

      return { success: true, data: decodedResponse.data, otherConfig: {hideNavbar: true}};
    } catch (error) {
      console.error('Token processing failed:', error);
      // Ensure auth process flag is cleared on error if requested
      if (shouldClearAuthFlag) {
        localStorage.removeItem('auth_process_active');
        localStorage.removeItem('auth_process_time');
        window.dispatchEvent(new CustomEvent('authProcessChange', { 
          detail: { isInAuthProcess: false } 
        }));
      }
      throw error;
    }
  };

  /**
   * Process basic credentials from query parameters
   */
  const processBasicCred = async (basicCred) => {
    try {
      // Decode basic credentials
      const decodedCred = atob(basicCred);
      const [username, password] = decodedCred.split(':');
      
      if (!username || !password) {
        throw new Error('Invalid basic credentials format');
      }

      // Call login API with basic credentials
      const response = await login({
        username: username.toLowerCase(),
        password: password
      }).unwrap();

      // Process the token received from login
      return await processToken(response.token, '', false, false);
    } catch (error) {
      console.error('Basic credentials processing failed:', error);
      throw error;
    }
  };

  /**
   * Clean up URL parameters after processing
   */
  const cleanUrl = () => {
    if (!cleanUrlAfterProcessing) return;
    
    const newUrl = new URL(window.location);
    const hasChanges = newUrl.searchParams.has(AUTH_MIDDLEWARE_CONFIG.queryParams.token) || newUrl.searchParams.has(AUTH_MIDDLEWARE_CONFIG.queryParams.basicCred);
    
    if (hasChanges) {
      newUrl.searchParams.delete(AUTH_MIDDLEWARE_CONFIG.queryParams.token);
      newUrl.searchParams.delete(AUTH_MIDDLEWARE_CONFIG.queryParams.basicCred);
      window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
    }
  };

  /**
   * Main middleware function to process query parameters
   */
  const processQueryAuth = async () => {
    if (isProcessingRef.current) return;
    
    const searchParams = new URLSearchParams(location.search);
    let token = searchParams.get(AUTH_MIDDLEWARE_CONFIG.queryParams.token);
    const basicCred = searchParams.get(AUTH_MIDDLEWARE_CONFIG.queryParams.basicCred);
    // Fallback: support token in route params for updateStatus route
    if (!token) {
      try {
        const path = location.pathname || '';
        // Expected: /updateStatus/:updateStatus/:tripId/:token
        const parts = path.split('/').filter(Boolean);
        if (parts[0] === 'updateStatus' && parts.length >= 4) {
          token = parts[3];
        }
      } catch (_) {
        // noop
      }
    }
    
    // Create a unique key for this request
    const requestKey = `${token || ''}-${basicCred || ''}-${location.pathname}`;
    
    if (processedParamsRef.current.has(requestKey)) {
      return;
    }

    if (!token && !basicCred) {
      return;
    }
    // console.log('shouldApplyMiddleware', shouldApplyMiddleware());
    if (!shouldApplyMiddleware()) {
     return;
    }

    isProcessingRef.current = true;
    processedParamsRef.current.add(requestKey);

    // Set auth process flag when starting authentication
    localStorage.setItem('auth_process_active', 'true');
    localStorage.setItem('auth_process_time', Date.now().toString());
    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('authProcessChange', { 
      detail: { isInAuthProcess: true } 
    }));

    try {
      let result;
      
      if (token) {
        console.log('Processing token from query parameters');
        result = await processToken(token, '', false, false);
      } else if (basicCred) {
        console.log('Processing basic credentials from query parameters');
        result = await processBasicCred(basicCred);
      }

      if (result?.success) {
        // Clean up URL parameters
        cleanUrl();

        // Clear auth process flag before calling success callback
        localStorage.removeItem('auth_process_active');
        localStorage.removeItem('auth_process_time');
        window.dispatchEvent(new CustomEvent('authProcessChange', { 
          detail: { isInAuthProcess: false } 
        }));

        if (onAuthSuccess) {
          onAuthSuccess(result);
        }
      }
    } catch (error) {
      console.error('Auth middleware failed:', error);
      
      // Clear auth process flag on error
      localStorage.removeItem('auth_process_active');
      localStorage.removeItem('auth_process_time');
      window.dispatchEvent(new CustomEvent('authProcessChange', { 
        detail: { isInAuthProcess: false } 
      }));
      
      if (onAuthError) {
        onAuthError(error);
      } else if (redirectToLoginOnFailure) {
        // Default error handling - redirect to login
        navigate('/login');
      }
    } finally {
      isProcessingRef.current = false;
    }
  };

  // Effect to run middleware when location changes
  useEffect(() => {
    processQueryAuth();
  }, [location.search, location.pathname]);

  return {
    processQueryAuth,
    shouldApplyMiddleware,
    isProcessing: isProcessingRef.current
  };
};

/**
 * Higher-order component to wrap components with auth middleware
 */
export const withAuthMiddleware = (Component, options = {}) => {
  return (props) => {
    const middleware = useAuthMiddleware(options);
    return <Component {...props} authMiddleware={middleware} />;
  };
};

export default useAuthMiddleware; 